import { createSlice } from '@reduxjs/toolkit'
import api from 'api/axios/foka'
import { createAxiosThunk } from 'utils/reduxUtil'
import Limitations from 'features/Limitations'
import { BizChannelTypes } from '../../Codes'
import { importCreative } from './import/creativeImportSlice'

const { BIZ_CHANNEL_MAX_CREATIVES } = Limitations

const initialState = {
  importedId: undefined,
  bizChannelType: BizChannelTypes.WEBSITE_BIZ_CHANNEL,
  channels: [],
  creatives: [],
  selectedChannel: null,
  isChannelsLoading: true,
  isCreativesLoading: true,
  channelsError: null,
  creativesError: null,
}

export const fetchChannelsAndCreative = createAxiosThunk(
  'creativeAdd/fetchChannelsAndCreative',
  async (creativeId, { getState, dispatch }) => {
    const response = await api.ad.getBizChannels()
    if (creativeId) {
      const channels = response.data.content
      const { selectedChannel } = getState().creative.creativeAdd
      const {
        data: { creative },
      } = await api.creative.getCreative(creativeId)
      const creativeChannel = channels.find(
        ({ id }) => id === creative.bizChannelId
      )
      if (creativeChannel) {
        if (
          selectedChannel === null ||
          selectedChannel?.id !== creative.bizChannelId
        ) {
          dispatch(selectChannel(creativeChannel))
        }
        await importCreative({
          creative,
          channel: creativeChannel,
          dispatch,
        })
      }
    }
    return response
  }
)

export const fetchCreatives = createAxiosThunk(
  'creativeAdd/fetchCreatives',
  (noArgs, { getState }) => {
    const { selectedChannel } = getState().creative.creativeAdd
    if (selectedChannel?.id) {
      return api.creative.getCreatives({
        bizChannelIds: [selectedChannel.id],
        page: 0,
        size: BIZ_CHANNEL_MAX_CREATIVES,
      })
    }
  }
)

const creativeAddSlice = createSlice({
  name: 'creativeAdd',
  initialState,

  reducers: {
    reset: () => initialState,
    selectChannel: (state, { payload: channel }) => {
      state.importedId = undefined
      state.selectedChannel = channel
      state.bizChannelType = channel.bizChannelType
    },
    setImportedId: (state, { payload: importedId }) => {
      state.importedId = importedId
    },
    setBizChannelType: (state, { payload: bizChannelType }) => {
      state.bizChannelType = bizChannelType
      state.selectedChannel = null
    },
    clearCreatives: state => {
      state.creatives = []
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchChannelsAndCreative.pending, state => {
      state.isChannelsLoading = true
    })

    builder.addCase(
      fetchChannelsAndCreative.fulfilled,
      (state, { payload }) => {
        state.channels = payload.content
        state.isChannelsLoading = false
      }
    )

    builder.addCase(fetchChannelsAndCreative.rejected, (state, { error }) => {
      state.channelsError = error || true
      state.isChannelsLoading = false
    })

    builder.addCase(fetchCreatives.pending, state => {
      state.isCreativesLoading = true
    })

    builder.addCase(fetchCreatives.fulfilled, (state, { payload }) => {
      state.creatives = payload.creatives.content.filter(
        creative => !creative.deleted
      )
      state.isCreativesLoading = false
    })

    builder.addCase(fetchCreatives.rejected, (state, { error }) => {
      state.creativesError = error || true
      state.isCreativesLoading = false
    })
  },
})

export const {
  reset,
  setImportedId,
  selectChannel,
  setBizChannelType,
  clearCreatives,
} = creativeAddSlice.actions

export default creativeAddSlice.reducer
